import React from 'react'
import { connect } from 'react-redux'
import { template } from 'lodash-es'
import { getHitsWithExtraData } from '../../redux/contentful-selectors'
import { FsCore } from '../../types/fs-core'
import { getProductTableRelations } from '../../utils/contentful'

import { Button, Select } from '../../atoms'
import { getField } from '../product-table/utils'
import { brand as brandSelector } from '../../redux/app-selectors'
import { getESData } from '../../redux/elasticsearch/elasticsearch-selectors'
import { ProductTableV2 } from './table'
import { Filters } from './filters'
import { useEntity } from '../helpers/selectors'
import { NoResults } from './no-results'

const ProductTableWrapperV2 = (props) => {
  const {
    complianceText,
    sorts,
    data,
    brand,
    totalHits,
    maxPerPage,
    filters,
    calculator,
    variant,
  } = props
  const sortBy = sorts.selectable.find((s) => s.default)?.label?.toLowerCase()
  const compiledComplianceText = template(complianceText)
  const updatedComplianceText = compiledComplianceText({
    resultCount: totalHits,
    resultOrResults: totalHits === 1 ? 'result' : 'results',
    complianceLink: '"/how-we-order-our-comparisons"',
    sortBy,
    transferAmount: calculator?.defaultInputs?.transferAmount,
    monthlyPayments: calculator?.defaultInputs?.monthlyPayments,
  })

  const sortOptions = sorts?.selectable?.length
    ? [
        { value: '', text: 'Sort' },
        ...sorts.selectable.map((sort) => {
          return { value: getField(sort.value)!, text: sort.label, default: sort.default }
        }),
      ]
    : []

  const remainingHits = totalHits - data.length
  const noResultsEntry = useEntity(props.noResultsTextBlock)

  const showResults = variant !== 'eligibility' || data.length > 0

  return (
    <div className='ProductTable' data-product-table-v2-wrapper data-variant={variant}>
      {variant === 'eligibility' && (
        <div
          data-product-table-v2-eligibility-modal
          className='ProductTable__eligibilityModal'
          style={{ display: 'none' }}
        >
          <div className='ProductTable__eligibilityModal__content'>
            <span className='ProductTable__eligibilityModal__close'>&times;</span>
            <p>
              Based on the details you entered today, the lender has pre-approved you for this card.
              Please note acceptance is subject to further lender, fraud and ID checks.
            </p>
          </div>
        </div>
      )}

      {showResults && (
        <div className='ProductTable__tableControls'>
          <div
            className='ProductTable__complianceText'
            dangerouslySetInnerHTML={{
              __html: `<div data-product-table-v2-compliance>${updatedComplianceText}</div>`,
            }}
          />

          <div className='ProductTable__tableControlsButtons'>
            {((filters && filters.length > 0) || (calculator && calculator?.fields?.length > 0)) &&
              !calculator?.isAlwaysVisible && (
                <Button variant='tertiary' size='md'>
                  Refine results
                </Button>
              )}
            <Select
              name='sort'
              value={sortOptions.find((sort) => sort.default).value}
              options={sortOptions}
              onChange={() => {}}
              brand={brand}
              styles={{
                backgroundImage:
                  brand === 'money'
                    ? 'url(/fs-core-assets/icons/money/sort.svg?v=13#sort)'
                    : `url(/fs-core-assets/icons/uswitch/sort.svg?v=13#sort),
        url(/fs-core-assets/icons/uswitch/chevron-down.svg?v=13#chevron-down)`,
                backgroundPosition: '10px, calc(100% - 10px) 50%',
                backgroundSize: brand === 'money' ? '14px, auto' : '16px, auto',
              }}
            />
          </div>
        </div>
      )}

      {filters && filters.length > 0 && <Filters filters={filters} />}
      {calculator && calculator?.fields?.length > 0 && (
        <div data-product-table-calculator>
          <Filters {...calculator} filters={calculator.fields} />
        </div>
      )}

      {showResults ? <ProductTableV2 {...props} /> : <NoResults {...noResultsEntry} />}

      {showResults && remainingHits > 0 && (
        <div className='ProductTable__loadMore' data-product-table-v2-load-more>
          <Button variant='primary' size='md'>
            Load {Math.min(remainingHits, maxPerPage)} more
          </Button>
        </div>
      )}
    </div>
  )
}

const ConnectTable = connect((state: FsCore.State, props) => {
  const { productTableSchema, productDataset } = getProductTableRelations(state, {
    fields: props,
  })

  const hits = getHitsWithExtraData(state, productTableSchema.sys.id, productDataset.sys?.id)
  const esData = getESData(state, productDataset.sys.id)

  const hitsWithEligibility = hits.map((hit) => {
    return {
      ...hit,
      eligibility: state.productTables.eligibilityResults?.find((e) => e.productId === hit._id),
    }
  })

  const hasEligibleHits = state.productTables.eligibilityResults?.length > 0

  const updatedHitsWithEligibility =
    props.variant === 'eligibility' && !hasEligibleHits ? [] : hitsWithEligibility

  const totalHits =
    (esData?.hits?.total as FsCore.Total).value !== undefined
      ? (esData?.hits?.total as FsCore.Total).value
      : esData?.hits?.total || 0

  const updatedTotalHits = props.variant === 'eligibility' && !hasEligibleHits ? 0 : totalHits

  const finalData =
    hits.length > 0 ? updatedHitsWithEligibility : productDataset?.fields?.data ?? []

  return {
    data: finalData,
    dataset: productDataset,
    schema: productTableSchema,
    contentfulId: props.sys.id,
    ...productTableSchema.fields.schema,
    brand: brandSelector(state),
    totalHits: updatedTotalHits,
    variant: props.variant,
    hasEligibleHits,
  }
})(ProductTableWrapperV2)

export default ConnectTable
